import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from 'react-icons/md';
import { IoMdAdd } from 'react-icons/io';
import { Layout, DeleteConfirmationModal} from '../../../../components';
import { handleApi, formatMediumDate, capitalize } from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { userData, currentColor, setSuppressAutoNavigate } = useContext(AppContext);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  const itemsPerPage = 10;
  const dropdownRef = useRef(null);
  useEffect(() => {
    const timeout = setTimeout(() => fetchQuotations(), 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const fetchQuotations = async () => {
    const apiEndpoint = 'sales/orders';
    setIsLoading(true);
    try {
      const response = await handleApi(apiEndpoint, {}, userData, 'GET', {});
      if (response.error) {
        toast.error(`Error: ${response.errorMessage}`);
        return;
      }
      console.log('response data sir', response.data);
      setQuotations(response.data);

    } catch (error) {
      toast.error(error.message || 'An unexpected error occurred.');
    } finally { setIsLoading(false); }
  };

  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const debouncedSearchTerm = useMemo(() => debounce(handleSearchChange, 300), []);

  const filteredQuotations = useMemo(() => {
    return quotations.filter((quote) =>
    quote?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [quotations, searchTerm]);

  const sortedFilteredQuotations = filteredQuotations.sort(
    (a, b) => new Date(b.create_time) - new Date(a.create_time)
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = sortedFilteredQuotations.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredQuotations.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    handleAction('delete', itemToDelete);
    console.log("Item deleted!");
    setIsModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
  };
  const handleAction = (action, quotation) => {
    setSuppressAutoNavigate(true); // prevent auto redirect
    switch (action) {
      case 'new':
        navigate(`/quotations/new`, { state: { quotations } }); // pass quotations
        break;
      case 'view':
        navigate(`/quotations/view/${quotation.id}`, { state: { initialQuotation: quotation } });
        break;
      case 'edit':
        navigate(`/quotations/edit/${quotation.id}`, { state: { initialQuotation: quotation, quotations } });
        break;
      case 'approve':
        navigate(`/quotations/approve/${quotation.id}`, { state: { initialQuotation: quotation } });
        break;
      case 'invoice':
          navigate(`/sales/invoices/new/${quotation.id}`, { state: { initialQuotation: quotation } });
          break;
      case 'delete':
        navigate(`/quotations/delete/${quotation.id}`, { state: { initialQuotation: quotation } });
        break;
      default: toast.error(`Action "${action}" is not supported.`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-white text-white  p-4 sm:p-8">
        {/* Header and Close Button */}
        <div className="relative flex justify-between items-center mb-4">
          <h1 className="font-extrabold text-gray-800 tracking-wide flex-grow text-center text-xl sm:text-2xl">
            QUOTATIONS
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading ? (
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Loading. Please wait...</p>
          </div>
        ) : (

          <div className="bg-gray-500 p-4 sm:p-6  rounded-3xl  border border-gray-300 mx-auto max-w-[60%]">
            {/* Search Input */}
            <div className="mb-4 flex justify-between items-center">
              <div className="relative w-full sm:w-1/2">
                {/* Emoji on the Left */}
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500 select-none">🔍</span>

                {/* Input Field */}
                <input
                  type="text"
                  placeholder="Search quotation"
                  onChange={debouncedSearchTerm}
                  className="w-full py-3 pl-10 pr-4 rounded-xl bg-gray-200 text-gray-800 placeholder-gray-500 shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white transition duration-300 text-base"
                />
              </div>
              {/* add new button */}
              <button
                className="text-white px-4 py-2 text-base font-semibold rounded-lg flex items-center gap-2 bg-blue-500 hover:bg-blue-700 transition-all duration-300"
                onClick={() => handleAction('new', {})}
              >
                <IoMdAdd size={24} />
                <span>New</span>
              </button>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap justify-center gap-2 mb-2 ">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => paginate(index + 1)}
                  className={`px-3 py-1 text-sm rounded-full font-semibold transition-colors duration-300
                              ${currentPage === index + 1 ? 'bg-cyan-500 text-white ' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Quotation Cards Display */}
            <div className="space-y-4">
              {currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <div key={item.id} className="relative">
                    <div
                      onClick={() => setSelectedRowId(item.id === selectedRowId ? null : item.id)}
                      className="flex items-center gap-4 mx-5 px-4 py-2 hover:bg-gray-700 transition-all duration-200
                       cursor-pointer rounded-2xl  border border-gray-100"
                    >
                      <div className="flex-shrink-0 flex flex-col items-center">
                        {/* Quotation Image */}
                        <img
                          className="rounded-full h-10 w-10 object-cover ring-2 ring-gray-700"
                          src={
                            item?.photo_file_name
                              ? `http://dev.neon-mw.com:3001/api/quotation_categories/photos/${item.photo_file_name}`
                              : `https://placehold.co/100x100/1f2937/e5e7eb?text=${
                            item?.partners?.name
                              ?.split(' ')
                              .map(word => word.charAt(0))
                              .join('')
                              .toUpperCase() || 'NP'
                            }`
                          }
                          alt={`${item?.partners?.name || 'Category'} image`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/100x100/1f2937/e5e7eb?text=${
                              item?.partners?.name
                                ?.split(' ')
                                .map(word => word.charAt(0))
                                .join('')
                                .toUpperCase() || 'NP'
                            }`;
                          }}
                        />

                        {/* Status */}
                        <span className={`mt-1 text-xs text-gray-200 font-medium 
                          ${item?.state?.toLowerCase() === 'draft' ? 'text-yellow-400 bg-yellow-900' :
                          item?.state?.toLowerCase() === 'approved' ? 'text-blue-400 bg-blue-900' :
                          item?.state?.toLowerCase() === 'invoiced' ? 'text-green-400 bg-green-900' :
                          item?.state?.toLowerCase() === 'deleted' ? 'text-gray-400 bg-gray-900' :
                          item?.state?.toLowerCase().includes('cancel') ? 'text-red-400 bg-red-900' : 'text-yellow-400 bg-yellow-900'}`}
                          title={item?.state?.toUpperCase() || 'N/A'}>{item?.state?.toUpperCase() || 'N/A'}
                        </span>
                      </div>

                      <div className="flex-grow text-white text-sm leading-tight">
                        {/* Quotation name + price in one row */}
                        <div className="flex items-center justify-between">
                          <span className="text-base text-white font-medium" title={capitalize(item?.name) || 'N/A'}>
                            {capitalize(item?.name) || 'N/A'} - {item?.reference || formatMediumDate(item?.date)}
                          </span>
                        </div>
                        <div className="flex-grow text-white text-sm">
                          <div className="flex flex-col">
                            {selectedRowId === item.id ? (
                              <div className="flex flex-col text-gray-300 text-xs leading-none">
                                <span className="my-1">Customer: {item?.partners?.name || '0'}</span>
                                <span>Value: {item?.totalDiscountedPriceInclusive || 'N/A'}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300 leading-none">
                                Customer: {item?.partners?.name || '0'} | Value: {item?.totalDiscountedPriceInclusive || 'N/A'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Conditionally render the menu or the arrow */}
                      <div className="flex-shrink-0 ml-auto flex flex-col items-end gap-1">
                        {selectedRowId === item.id ? (
                          item.approved === 1 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAction('view', item);
                              }}
                              className="px-2 py-1 text-xs rounded-lg bg-gray-600 hover:bg-gray-500 transition-all duration-300"
                            >
                              View
                            </button>
                          ) : (
                            <>
                               {item?.state?.toLowerCase() !== 'invoiced' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAction('edit', item);
                                }}
                                className="px-2 py-1 text-xs rounded-lg bg-gray-600 hover:bg-gray-500 transition-all duration-300"
                              >
                                Edit
                              </button>
                               )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAction('view', item);
                                }}
                                className="px-2 py-1 text-xs rounded-lg bg-gray-600 hover:bg-gray-500 transition-all duration-300"
                              >
                                View
                              </button>
                              {/* Conditionally render the "Approve" button if the item is not approved */}
                              {item?.state?.toLowerCase() !== 'approved' && item?.state?.toLowerCase() !== 'invoiced' && (
                              
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction('approve', item);
                                  }}
                                  className="px-2 py-1 text-xs rounded-lg bg-green-600 hover:bg-green-500 transition-all duration-300"
                                >
                                  Approve
                                </button>
                              )}

                              {item?.state?.toLowerCase() !== 'invoiced' && item?.state?.toLowerCase() !== 'draft' && (
                            
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAction('invoice', item);
                                  }}
                                  className="px-2 py-1 text-xs rounded-lg bg-blue-600 hover:bg-blue-500 transition-all duration-300"
                                >
                                  Invoice
                                </button>
                              )}
                              
                              {item?.state?.toLowerCase() !== 'invoiced' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(item);

                                  }}
                                  className="px-2 py-1 text-xs rounded-lg bg-red-600 hover:bg-red-500 transition-all duration-300"
                                >
                                  Cancel
                                </button>
                              )}
                            </>
                          )
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 transform transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-white/50 py-10">
                  <p className="text-lg">No quotation found.</p>
                </div>
              )}
            </div>
            {console.log('Current Items for display sir :', currentItems)}
          </div>
        )}
      </div>
      {isModalOpen && (
        <DeleteConfirmationModal
          title="Cancel/Delete Quotation"
          message={`This will navigate to the delete page for quotation: ${itemToDelete?.name || itemToDelete?.reference}.`}
          actionLabel="Proceed to Delete"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          itemName={itemToDelete?.name || itemToDelete?.reference}
        />
      )}
    </Layout>
  );
};
export default Quotations;